import type { DateValidationError } from '@mui/x-date-pickers';
import type { DatePickerProps as PantryDatePickerProps } from '@side/pantry';
import { format, isValid, parseISO } from 'date-fns';
import { useState } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import { DatePickerWithEventTracking } from '@/components/DatePicker/DatePickerV2';
import type { RHFBoundProps } from './types';

export type DateFieldProps<TFieldValues extends FieldValues = FieldValues> =
  Omit<
    PantryDatePickerProps,
    'value' | 'onChange' | 'onAccept' | 'name' | 'onBlur'
  > &
    RHFBoundProps<TFieldValues, string | null | undefined> & {
      label: string;
      required?: boolean;
      fullWidth?: boolean;
      /** Apply Pantry's required-empty highlight while the field is empty. */
      highlightWhenEmpty?: boolean;
      /** Analytics container; defaults to `form-field-${name}`. */
      eventContainer?: string;
      /** Fires when the user accepts a date, with the new ISO string. */
      onAccept?: (value: string | null) => void;
      onBlur?: () => void;
    };

const formatDate = (value: Date | null) =>
  value && isValid(value) ? format(value, 'yyyy-MM-dd') : null;

const DEFAULT_DISPLAY_FORMAT = 'MM/dd/yy';

interface RangeBounds {
  minDate?: Date;
  maxDate?: Date;
  displayFormat: string;
}

const rangeMessageFor = (
  reason: DateValidationError,
  { minDate, maxDate, displayFormat }: RangeBounds,
) => {
  if (reason === 'maxDate' && maxDate) {
    return `Cannot be after ${format(maxDate, displayFormat)}`;
  }
  if (reason === 'minDate' && minDate) {
    return `Cannot be before ${format(minDate, displayFormat)}`;
  }
  if (reason === 'disableFuture') {
    return 'Cannot be a future date';
  }
  if (reason === 'disablePast') {
    return 'Cannot be a past date';
  }
  return null;
};

/**
 * Date picker. Stores ISO `yyyy-MM-dd` strings (or `null`) — not `Date`
 * objects, so the value is API-ready and JSON-serializable without further
 * transforms. Forwards remaining `DatePicker` props (including `slotProps`)
 * to the underlying Pantry component.
 *
 * Range bounds (`minDate`, `maxDate`, `disablePast`, `disableFuture`) explain
 * themselves: a violation renders a message derived from the bound. A `rules`
 * message wins over it, so a field can pair a bound with its own copy.
 */
export function DateField<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  rules,
  shouldUnregister,
  label,
  required,
  fullWidth,
  highlightWhenEmpty,
  eventContainer,
  slotProps,
  errorMessage,
  onError,
  onAccept: consumerOnAccept,
  onBlur: consumerOnBlur,
  ...rest
}: DateFieldProps<TFieldValues>) {
  const {
    field: { value, onChange, onBlur: rhfOnBlur },
    fieldState: { error, invalid },
  } = useController({ control, name, rules, shouldUnregister });
  const [rangeError, setRangeError] = useState<DateValidationError>(null);

  const dateValue = typeof value === 'string' && value ? parseISO(value) : null;
  const textFieldSlotProps =
    typeof slotProps?.textField !== 'function'
      ? slotProps?.textField
      : undefined;

  const rangeMessage = rangeMessageFor(rangeError, {
    minDate: rest.minDate,
    maxDate: rest.maxDate,
    displayFormat: rest.format ?? DEFAULT_DISPLAY_FORMAT,
  });

  return (
    <DatePickerWithEventTracking
      {...rest}
      eventContainer={eventContainer ?? `form-field-${name}`}
      value={dateValue}
      errorMessage={error?.message || rangeMessage || errorMessage}
      onError={(reason, errorValue) => {
        setRangeError(reason);
        onError?.(reason, errorValue);
      }}
      onChange={(date) => onChange(formatDate(date))}
      onAccept={(date) => {
        const formatted = formatDate(date);
        onChange(formatted);
        consumerOnAccept?.(formatted);
      }}
      slotProps={{
        ...slotProps,
        textField: {
          ...textFieldSlotProps,
          name,
          label,
          required,
          fixedHeight: true,
          fullWidth,
          onBlur: () => {
            rhfOnBlur();
            consumerOnBlur?.();
          },
          error: invalid,
          helperText: error?.message ?? textFieldSlotProps?.helperText,
          highlight:
            highlightWhenEmpty && (value == null || value === '')
              ? 'secondary'
              : undefined,
        },
      }}
    />
  );
}
